"use client";

import React, { ReactNode } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useOptionalAuth } from "@vellum/micro-app-auth";
import { cn } from "../lib/utils";
import { Logo } from "./Logo";

/**
 * Vellum-defined accent colors. Give each app its own so it is easy to notice
 * at a glance which app you are in. The strings must stay whole class names —
 * Tailwind cannot see classes assembled at runtime.
 */
const headerAccents = {
  green: {
    rule: "bg-green-700",
    brand: "text-green-700",
    nav: "text-green-700",
  },
  blue: { rule: "bg-blue-600", brand: "text-blue-600", nav: "text-blue-600" },
  purple: {
    rule: "bg-purple-600",
    brand: "text-purple-600",
    nav: "text-purple-600",
  },
  amber: {
    rule: "bg-amber-600",
    brand: "text-amber-600",
    nav: "text-amber-600",
  },
  teal: { rule: "bg-teal-600", brand: "text-teal-600", nav: "text-teal-600" },
  rose: { rule: "bg-rose-600", brand: "text-rose-600", nav: "text-rose-600" },
} as const;

type HeaderAccent = keyof typeof headerAccents;

interface HeaderNavItem {
  label: ReactNode;
  href: string;
  active?: boolean;
}

/**
 * Segment-aware active match, equivalent to what react-router's `NavLink` does:
 * `/my-payroll` matches itself and `/my-payroll/123`, but not `/my-payroll-history`.
 */
export function isNavItemActive(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

interface RenderLinkArgs {
  href: string;
  className: string;
  children: ReactNode;
}

/**
 * Renders an internal link. Defaults to a plain anchor so the component stays
 * framework-agnostic; pass a router-aware link (react-router `Link`, `next/link`)
 * to keep client-side navigation.
 */
type RenderLink = (args: RenderLinkArgs) => ReactNode;

const defaultRenderLink: RenderLink = ({ href, className, children }) => (
  <a href={href} className={className}>
    {children}
  </a>
);

interface AppHeaderProps {
  /** Wordmark text, e.g. "Vellum Payroll". Omit when using `left` instead. */
  brand?: ReactNode;
  /** Where the wordmark links to. */
  brandHref?: string;
  /** Per-app accent color, so each app looks distinct. */
  accent?: HeaderAccent;
  navItems?: HeaderNavItem[];
  renderLink?: RenderLink;
  /**
   * Extra content on the left, after the wordmark and nav — e.g. breadcrumbs.
   */
  left?: ReactNode;
  /** Extra content on the right, before the user dropdown. */
  actions?: ReactNode;
  /** Renders a hamburger button, shown below the `lg` breakpoint. */
  onToggleSidebar?: () => void;
  className?: string;
  showLogo?: boolean;
}

const navItemClass = (active: boolean, accent: HeaderAccent) =>
  cn(
    "text-sm font-medium no-underline transition-colors",
    active ? headerAccents[accent].nav : "text-gray-600 hover:text-blue-500",
  );

export function AppHeader({
  brand,
  brandHref = "/",
  accent = "green",
  navItems,
  renderLink = defaultRenderLink,
  left,
  onToggleSidebar,
  className,
  showLogo = false,
}: AppHeaderProps) {
  // Null in apps that mount no AuthProvider (Customer Platform has its own
  // session), which simply renders no user dropdown.
  const auth = useOptionalAuth();
  return (
    <header
      className={cn(
        "sticky top-0 z-40 flex h-14 shrink-0 items-center justify-between gap-x-4 border-b border-gray-200 bg-white px-4 shadow-xs sm:gap-x-6 sm:px-6 lg:px-8",
        className,
      )}
    >
      <div className="flex flex-row items-baseline min-w-0 gap-4 md:gap-6">
        {onToggleSidebar && (
          <button
            type="button"
            aria-label="Open sidebar"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={onToggleSidebar}
          >
            <span className="iconoir-menu" />
          </button>
        )}
        {showLogo && (
          <Logo
            variant="dark"
            size="small"
            // The negative margin aligns the brand text with the "Vellum" word in the logo
            className="mb-[-2.21px] sm:mb-[-3.32px]"
          />
        )}

        {brand &&
          renderLink({
            href: brandHref,
            className: cn(
              "text-lg md:text-xl font-semibold text-green-700",
              headerAccents[accent].brand,
            ),
            children: brand,
          })}

        {navItems && navItems.length > 0 && (
          <nav className="flex items-center gap-4">
            {navItems.map((item) => (
              <React.Fragment key={item.href}>
                {renderLink({
                  href: item.href,
                  className: navItemClass(Boolean(item.active), accent),
                  children: item.label,
                })}
              </React.Fragment>
            ))}
          </nav>
        )}

        {left}
      </div>

      {auth?.user && (
        <Menu as="div" className="relative">
          <MenuButton className="-m-1.5 flex items-center p-1.5">
            <span className="flex items-center">
              <span
                className="flex mr-4 text-sm font-semibold leading-6 text-gray-900"
                aria-hidden="true"
              >
                {auth.user.name}
              </span>
              <span className="iconoir-nav-arrow-down" />
            </span>
          </MenuButton>

          <MenuItems className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-xs ring-1 ring-gray-900/5 focus:outline-hidden">
            <MenuItem>
              <button
                type="button"
                onClick={() => {
                  void auth.signOut();
                }}
                className="block w-full px-3 py-1 text-sm leading-6 text-gray-800 hover:bg-gray-100 text-left"
              >
                Logout
              </button>
            </MenuItem>
          </MenuItems>
        </Menu>
      )}
    </header>
  );
}

export {
  headerAccents,
  type AppHeaderProps,
  type HeaderAccent,
  type HeaderNavItem,
  type RenderLink,
};
